'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  isVoiceAnswerable,
  RendererOption,
} from '@/shared-project-response-message-renderers';
import {
  MessageFormValues,
  SubmitMessageOptions,
  useProjectResponseModeContext,
} from '@/shared-survey';
import {
  AUDIO_QUEUE_STOP_CURRENT_EVENT,
  WAVEFORM_PLAYER_CUSTOM_STOP_EVENT,
} from '@/shared-ui';
import { playAckSound } from '@/shared-utilities';
import { ProjectMode } from '@/survey-graphql';

import {
  clearContentOnQuestionTypeChange,
  focusContentFieldWhenEnabled,
} from './project-response-form.helpers';
import {
  removeLastOccurenceAndEverythingAfter,
  enumStringEquals,
  shouldStopMicOnManualSubmit,
} from './project-response.helpers';

/** Question types where "Next" only is valid (URL, Task, Message / usabilityNext). */
function isNextOnlyQuestionType(
  questionType: RendererOption | null | undefined
): boolean {
  if (!questionType) return false;
  return ['usabilityNext', 'url', 'message', 'task'].includes(questionType);
}

export function useProjectResponseForm({
  loading,
  disabled,
  questionType,
  onSubmit,
}: {
  loading?: boolean;
  disabled?: boolean;
  questionType?: RendererOption | null;
  onSubmit: (
    values: MessageFormValues,
    options?: SubmitMessageOptions
  ) => Promise<void>;
}) {
  const {
    formState: { isSubmitting },
    handleSubmit,
    register,
    setFocus,
    control,
    setValue,
  } = useFormContext<MessageFormValues>();

  const {
    projectMode,
    stopListening,
    currentTranscript,
    setCurrentTranscript,
    eventsObservable,
    sendForceEndpoint,
  } = useProjectResponseModeContext();

  const prevQuestionTypeRef = useRef<RendererOption | null | undefined>(
    questionType
  );

  useEffect(
    () => focusContentFieldWhenEnabled({ disabled, setFocus }),
    [disabled, setFocus]
  );

  useEffect(
    () =>
      clearContentOnQuestionTypeChange({
        prevTypeRef: prevQuestionTypeRef,
        questionType,
        setValue,
      }),
    [questionType, setValue]
  );

  const { responseMode } = useProjectResponseModeContext();

  const resetAndSubmit = useCallback(
    async (data: MessageFormValues, options?: SubmitMessageOptions) => {
      const content = data.content?.trim();

      if (!content) {
        setValue('content', '');
        if (responseMode === 'text' || !isVoiceAnswerable(questionType)) {
          return;
        }
      }

      try {
        window.dispatchEvent(new Event(WAVEFORM_PLAYER_CUSTOM_STOP_EVENT));
        window.dispatchEvent(new Event(AUDIO_QUEUE_STOP_CURRENT_EVENT));

        const transcriptCandidate = (currentTranscript ?? '').trim();
        const enrichedData: MessageFormValues =
          responseMode === 'voice' &&
          !data.transcript &&
          transcriptCandidate.length > 0
            ? { ...data, transcript: transcriptCandidate }
            : data;

        if (shouldStopMicOnManualSubmit(responseMode, options)) {
          await stopListening();
        }

        await handleSubmit(() => {
          setValue('content', '');
          return onSubmit(enrichedData, options);
        })();

        if (questionType === 'ranking') {
          setValue('content', '');
        }
        setTimeout(() => setFocus('content'), 0);
      } catch {
        setValue('content', content);
      }
    },
    [
      currentTranscript,
      handleSubmit,
      onSubmit,
      questionType,
      responseMode,
      setFocus,
      setValue,
      stopListening,
    ]
  );

  useEffect(() => {
    const subscription = eventsObservable.subscribe((event) => {
      if (
        event?.type !== 'next_question' ||
        isSubmitting ||
        !isVoiceAnswerable(questionType)
      ) {
        return;
      }

      // Prefer currentTranscript (live React state from local mic streaming).
      // Fall back to event.transcript for mobile remote voice, where
      // setCurrentTranscript was called synchronously just before this event
      // fires and React state hasn't flushed yet (stale closure).
      const transcript = (currentTranscript || event.transcript)?.trim() ?? '';
      const newTranscript = removeLastOccurenceAndEverythingAfter(
        transcript,
        event.triggerWord ?? ''
      );

      if (newTranscript.trim()) {
        setCurrentTranscript(newTranscript);
        void resetAndSubmit(
          { content: '', transcript: newTranscript },
          { keepListening: true }
        );
        sendForceEndpoint();
        if (enumStringEquals(projectMode, ProjectMode.HandsFree)) {
          playAckSound();
        }
        return;
      }

      // HandsFree UT: "Next" only → submit only for URL/Task/Message questions (from context)
      const isNextOnlyValidHere =
        enumStringEquals(projectMode, ProjectMode.HandsFree) &&
        event.triggerWord &&
        isNextOnlyQuestionType(questionType);
      if (isNextOnlyValidHere) {
        setCurrentTranscript('');
        void resetAndSubmit(
          { content: '', transcript: '' },
          { keepListening: true }
        );
        sendForceEndpoint();
        playAckSound();
      }
    });
    return () => subscription.unsubscribe();
  }, [
    currentTranscript,
    eventsObservable,
    isSubmitting,
    projectMode,
    questionType,
    resetAndSubmit,
    sendForceEndpoint,
    setCurrentTranscript,
  ]);

  const submitOnEnter = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSubmit((data) => resetAndSubmit(data))();
    }
  };

  return {
    register,
    setValue,
    submitOnEnter,
    isSubmitting: loading || isSubmitting,
    control,
    resetAndSubmit,
    handleSubmit,
  };
}
